import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import getJob from '@cartwhl/common/api/getJob';
import {
  FromStore,
  GenerationType,
  Project,
  Variation,
} from '@cartwhl/common/types';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import CircleArrow from '@cartwhl/ui/icons/CircleArrow';
import Button from '@cartwhl/ui/input/Button';
import GenerationButton from '@cartwhl/ui/molecules/GenerationButton';
import Modal from '@cartwhl/ui/molecules/Modal';

import { animationPlayerStore, noticesStore, projectStore } from 'stores';

import CurrentMotion from './CurrentMotion';
import EditableMotionName from './EditableMotionName';
import VariantionPicker from './VariantionPicker';

export default function EditMotionModal({
  close,
  payload,
}: {
  close: () => void;
  payload: { jobID: string; objectID: string };
}) {
  const {
    currentProject,
    motion: { timelineData },
  } = useSnapshot(projectStore.state);
  const mainPlayerStore = animationPlayerStore.defaultInstance;
  const editMotionPlayerStore =
    animationPlayerStore.getAnimationPlayerStoreByID('edit-motion');

  const { duration } = useSnapshot(editMotionPlayerStore.state);

  const frameRate = 30;

  const currentTimelineItem = timelineData?.[payload.objectID]?.find(
    (item) => item.jobID === payload.jobID,
  );

  const [tempItemData, setTempItemData] = useState<{
    title?: string;
    trimStart?: number | null;
    trimEnd?: number | null;
    variationNumber?: number;
  }>({
    title: currentTimelineItem?.title,
    trimStart: currentTimelineItem?.trimStart,
    trimEnd: currentTimelineItem?.trimEnd,
    variationNumber: currentTimelineItem?.variationNumber ?? 0,
  });

  const [variations, setVariations] = useState<
    Record<string, Variation> | undefined
  >(undefined);

  const currentVariation =
    variations?.[tempItemData.variationNumber ?? 0] ?? variations?.[0];

  const subTitle =
    currentTimelineItem?.generationType === GenerationType.MOTION_FROM_TEXT
      ? 'Select animation to edit:'
      : 'Edit animation:';

  const [submittingMoreVars, setSubmittingMoreVars] = useState(false);

  const meshPath = currentProject?.data?.characters[payload.objectID].meshURL;

  useEffect(() => {
    // Disable the main player while editing the motion
    mainPlayerStore.setDisabled(true);
    return () => {
      mainPlayerStore.setDisabled(false);
      animationPlayerStore.removeAnimationPlayerStoreByID('edit-motion');
    };
  }, []);

  const handleSave = () => {
    const hasChanges =
      tempItemData.trimStart !== currentTimelineItem?.trimStart ||
      tempItemData.trimEnd !== currentTimelineItem?.trimEnd ||
      tempItemData.variationNumber !==
        (currentTimelineItem?.variationNumber ?? 0) ||
      ('title' in tempItemData &&
        tempItemData.title !== currentTimelineItem?.title);

    if (hasChanges) {
      projectStore.setTimelineItemData(
        payload.objectID,
        payload.jobID,
        tempItemData,
      );
      projectStore.triggerStitching(payload.objectID);
    }
    close();
  };

  useEffect(() => {
    const loadVariations = async (jobID: string) => {
      const { data, error } = await getJob(jobID);

      if (error) {
        noticesStore.error('Failed to load variations');
        return;
      }

      setVariations(data?.variationInfoByID);
    };

    loadVariations(payload.jobID);
  }, [payload.jobID]);

  const handleFavoriteToggle = (animationNumber: string, favorite: boolean) => {
    setVariations((prev) =>
      prev
        ? { ...prev, [animationNumber]: { ...prev[animationNumber], favorite } }
        : prev,
    );
  };

  return (
    <Modal verticalBleed horizontalBleed open maxWidth={672}>
      <div className="flex flex-col gap-3">
        <div className="flex justify-between gap-1 px-4 pt-4">
          <div className="mt-[-6px] flex flex-1 flex-col gap-1">
            {currentTimelineItem?.title && (
              <EditableMotionName
                motionName={tempItemData.title ?? currentTimelineItem.title}
                onSave={(title) => {
                  setTempItemData((obj) => ({
                    ...obj,
                    title,
                  }));
                }}
              />
            )}
            <Text
              variant="studio/body/sm"
              className="text-gray-60 dark:text-gray-45"
            >
              {subTitle}
            </Text>
          </div>
          <div className="flex-none">
            {/* <GenerateMoreButton
              submitting={submittingMoreVars}
              isRegenerating={isRegenerating}
              progressPercentage={progressPercentage}
              onClick={() => submitVariations(selectedVariationNumber)}
            /> */}
          </div>
        </div>
        {variations && currentTimelineItem ? (
          <div className="flex flex-col gap-3 px-4">
            {Object.keys(variations).length > 1 && meshPath && (
              <VariantionPicker
                jobID={payload.jobID}
                meshPath={meshPath}
                variations={variations}
                selectedVariation={tempItemData.variationNumber ?? 0}
                setSelectedVariation={(variationNumber) => {
                  setTempItemData((obj) => ({
                    ...obj,
                    variationNumber,
                  }));
                }}
                onFavoriteToggle={handleFavoriteToggle}
              />
            )}
            {currentVariation && (
              <CurrentMotion
                currentVariation={currentVariation}
                frameRate={frameRate}
                currentTimelineItem={currentTimelineItem}
                variationNumber={tempItemData.variationNumber ?? 0}
                onFavoriteToggle={handleFavoriteToggle}
                onTrimChange={(trimStart, trimEnd) => {
                  let newTrimStart = null;
                  if (typeof trimStart === 'number' && trimStart !== 0) {
                    newTrimStart = trimStart / frameRate; // convert frames to seconds
                  }

                  let newTrimEnd = null;
                  if (
                    typeof trimEnd === 'number' &&
                    trimEnd !== duration.originalFrames
                  ) {
                    newTrimEnd = trimEnd / frameRate; // convert frames to seconds
                  }
                  setTempItemData((obj) => ({
                    ...obj,
                    trimStart: newTrimStart,
                    trimEnd: newTrimEnd,
                  }));
                }}
              />
            )}
          </div>
        ) : (
          <div className="flex h-[400px] items-center justify-center">
            <Loading className="text-green h-10 w-10" />
          </div>
        )}
        <div className="flex justify-between px-4 pt-1 pb-4">
          <Button
            variant="secondary-destructive"
            size="sm"
            onClick={() => {
              projectStore.removeFromTimeline(payload.objectID, payload.jobID);
              close();
            }}
          >
            Remove from timeline
          </Button>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={close}>
              Close
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave}>
              Done
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

const getCurrentTimelineItem = (
  project: FromStore<Project> | undefined,
  jobID: string,
  objectID: string,
) => {
  if (!project || !project.characters[objectID]) return undefined;
  return project.characters[objectID].timeline.find(
    (item) => item.jobID === jobID,
  );
};

function GenerateMoreButton({
  submitting,
  isRegenerating,
  progressPercentage,
  onClick,
}: {
  submitting: boolean;
  isRegenerating: boolean;
  progressPercentage: number;
  onClick: () => void;
}) {
  return (
    <GenerationButton
      progress={
        submitting
          ? {
              percentage: 0,
              statusText: 'Submitting...',
            }
          : isRegenerating
            ? {
                percentage: progressPercentage,
                statusText: `Generating...${progressPercentage}%`,
              }
            : undefined
      }
      icon={<CircleArrow />}
      onClick={onClick}
    >
      Generate more
    </GenerationButton>
  );
}
